import React, { useRef, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Footer from "../components/Footer/Footer";

import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  LinearProgress,
  styled,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "../components/Navbar";
import {
  LocationOn,
  CalendarToday,
  MonetizationOn,
  TrendingUp,
  Agriculture,
  CheckCircle,
  LocalFlorist,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { project_details } from "./project";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "16px 16px 0 0",
  overflow: "hidden",
  boxShadow: "none",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

// Compact Detail Card Component
const CompactDetailCard = ({ icon, label, value, color = "#36b39a" }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: 1.5,
      borderRadius: "12px",
      bgcolor: "rgba(54, 179, 154, 0.05)",
      border: "1px solid rgba(54, 179, 154, 0.1)",
      transition: "all 0.3s ease",
      "&:hover": {
        bgcolor: "rgba(54, 179, 154, 0.1)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(54, 179, 154, 0.15)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: `${color}15`,
        color: color,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="600" sx={{ fontSize: "0.9rem" }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

// Compact Crop Chip
const CompactCropChip = ({ name, expenses }) => (
  <Chip
    icon={<LocalFlorist sx={{ fontSize: "18px" }} />}
    label={
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight="600">
          {name}
        </Typography>
        {expenses > 0 && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            • ₹{expenses.toLocaleString()}
          </Typography>
        )}
      </Box>
    }
    sx={{
      bgcolor: "rgba(54, 179, 154, 0.08)",
      color: "#23766a",
      fontWeight: "500",
      py: 2,
      px: 1,
      borderRadius: "10px",
      border: "1px solid rgba(54, 179, 154, 0.15)",
      transition: "all 0.3s ease",
      "&:hover": {
        bgcolor: "rgba(54, 179, 154, 0.15)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 8px rgba(54, 179, 154, 0.2)",
      },
    }}
  />
);

const MoreDetails = () => {
  const footerRef = useRef(null);
  const { state } = useLocation();
  const project = project_details.data.projects[0];
  const crops = project_details.data.crops || [];
  const cropExpenses = project_details.data.crop_expenses || [];
  const contactUsRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [openSignup, setOpenSignup] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    pincode: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [state]);

  const handleContactUsClick1 = () => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!project) {
    return <Typography>Project data not found</Typography>;
  }

  const getCropExpenses = (cropId) => {
    const expense = cropExpenses.find((exp) => exp.Crop_ID === cropId);
    if (!expense) return null;

    try {
      const cleanExpenseString = expense.Expense_Type.replace(/'$/, "");
      return JSON.parse(cleanExpenseString);
    } catch (e) {
      console.error("Error parsing expense:", e);
      return null;
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!signupData.name.trim()) errors.name = "Name is required";
    if (!signupData.email) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email))
      errors.email = "Invalid email format";

    if (!signupData.phone) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(signupData.phone))
      errors.phone = "Phone must be 10 digits";

    if (!signupData.state.trim()) errors.state = "State is required";
    if (!signupData.city.trim()) errors.city = "City is required";

    if (!signupData.pincode) errors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(signupData.pincode))
      errors.pincode = "Pincode must be 6 digits";

    if (!signupData.password) errors.password = "Password is required";
    if (!signupData.confirmPassword)
      errors.confirmPassword = "Confirm password is required";
    else if (signupData.password !== signupData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateTotalExpenses = (expenses) => {
    if (!expenses) return 0;
    return Object.values(expenses).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0
    );
  };

  const handleInvest = () => navigate("/disclaimer");

  const handleContactUsClick = () => {
    window.open("https://wa.me/919353511090", "_blank");
    if (!contactUsRef.current) {
      contactUsRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSignupOpen = () => setOpenSignup(true);
  const handleSignupClose = () => setOpenSignup(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));

    if (name === "password" || name === "confirmPassword") {
      setPasswordError(
        name === "confirmPassword"
          ? value !== signupData.password
          : signupData.confirmPassword !== value
      );
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (signupData.password !== signupData.confirmPassword) {
      setPasswordError(true);
      return;
    }

    setPasswordError(false);
    setApiError("");
    setLoading(true);

    try {
      const response = await fetch("http://devapi/api/users/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Name: signupData.name,
          Email: signupData.email,
          Mobile_Number: signupData.phone,
          State: signupData.state,
          City: signupData.city,
          Pincode: signupData.pincode,
          Password: signupData.confirmPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Signup failed");
      }

      toast.success("Signup successful!");
      navigate("/disclaimer", { state: { signupData } });
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fundingProgress =
    project.current_funding && project.funding_goal
      ? (project.current_funding / project.funding_goal) * 100
      : 0;

  return (
    <HelmetProvider>
      <Navbar onContactUsClick={handleContactUsClick} />

      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          "& .MuiPaper-root": {
            boxShadow: "none !important",
          },
          "& .MuiCard-root": {
            boxShadow: "none !important",
          },
        }}
      >
        <StyledPaper elevation={0}>
          <Box
            component="img"
            src={project.Image_URL}
            alt={project.Name}
            sx={{
              width: "100%",
              height: isMobile ? 120 : 400,
              objectPosition: "center",
              p: 4,
            }}
          />

          <Box sx={{ p: { xs: 2, md: 10 } }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingBottom={2}
            >
              <Typography
                variant="h4"
                fontSize={isMobile ? "18px" : "34px"}
                fontWeight="bold"
                gutterBottom
                sx={{ color: "#23766a", margin: 0 }}
              >
                {project.Name}
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: "divider" }} />

            {/* Funding Progress */}
            {project.funding_goal > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, rgba(35, 118, 106, 0.05) 0%, rgba(54, 179, 154, 0.05) 100%)",
                  border: "1px solid rgba(54, 179, 154, 0.1)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Funding Progress
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="#36b39a">
                      {fundingProgress.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={fundingProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: "rgba(54, 179, 154, 0.1)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        background: "linear-gradient(90deg, #23766a 0%, #36b39a 100%)",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      ₹{project.current_funding.toLocaleString()} raised
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Goal: ₹{project.funding_goal.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Compact Project Details Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: "16px",
                background:
                  "linear-gradient(135deg, rgba(35, 118, 106, 0.05) 0%, rgba(54, 179, 154, 0.05) 100%)",
                border: "1px solid rgba(54, 179, 154, 0.1)",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="#23766a"
                  sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                >
                  Project Overview
                </Typography>
                
                {/* Status Badge */}
                <Chip
                  icon={<CheckCircle fontSize="small" />}
                  label={project.Status === "Open" ? "Active Project" : "Completed Project"}
                  sx={{
                    bgcolor: project.Status === "Open" 
                      ? "rgba(76, 175, 80, 0.15)" 
                      : "linear-gradient(135deg, rgba(158, 158, 158, 0.15) 0%, rgba(117, 117, 117, 0.15) 100%)",
                    color: project.Status === "Open" ? "#2e7d32" : "#616161",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    px: 1.5,
                    py: 2.5,
                    border: project.Status === "Open" 
                      ? "2px solid rgba(76, 175, 80, 0.3)" 
                      : "2px solid rgba(158, 158, 158, 0.3)",
                    boxShadow: project.Status === "Open"
                      ? "0 2px 8px rgba(76, 175, 80, 0.2)"
                      : "0 2px 8px rgba(0, 0, 0, 0.1)",
                    "& .MuiChip-icon": {
                      color: project.Status === "Open" ? "#2e7d32" : "#616161",
                    },
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                {/* Project Details */}
                <Grid item xs={6} sm={3}>
                  <CompactDetailCard
                    icon={<LocationOn fontSize="small" />}
                    label="Location"
                    value={project.Location}
                    color="#36b39a"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactDetailCard
                    icon={<Agriculture fontSize="small" />}
                    label="Area"
                    value={`${project.Area} acres`}
                    color="#23766a"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactDetailCard
                    icon={<CalendarToday fontSize="small" />}
                    label="Tenure"
                    value={`${project.Tenure} years`}
                    color="#36b39a"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactDetailCard
                    icon={<CalendarToday fontSize="small" />}
                    label="Duration"
                    value={`${project.duration_months} months`}
                    color="#23766a"
                  />
                </Grid>

                {/* Financial Details */}
                <Grid item xs={6} sm={3}>
                  <CompactDetailCard
                    icon={<MonetizationOn fontSize="small" />}
                    label="Min Investment"
                    value={`₹${project.Min_Investment.toLocaleString()}`}
                    color="#36b39a"
                  />
                </Grid>
                {project.funding_goal > 0 && (
                  <Grid item xs={6} sm={3}>
                    <CompactDetailCard
                      icon={<TrendingUp fontSize="small" />}
                      label="Funding Goal"
                      value={`₹${project.funding_goal.toLocaleString()}`}
                      color="#23766a"
                    />
                  </Grid>
                )}
                {project.return_rate > 0 && (
                  <Grid item xs={6} sm={3}>
                    <CompactDetailCard
                      icon={<TrendingUp fontSize="small" />}
                      label="Return Rate"
                      value={`${project.return_rate}%`}
                      color="#36b39a"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Compact Crops Section - Only Names */}
            {crops.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 4,
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, rgba(35, 118, 106, 0.05) 0%, rgba(54, 179, 154, 0.05) 100%)",
                  border: "1px solid rgba(54, 179, 154, 0.1)",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Agriculture sx={{ color: "#36b39a", fontSize: "24px" }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="#23766a"
                    sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                  >
                    Crops Portfolio
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {crops.map((crop) => {
                    const expenses = getCropExpenses(crop.ID);
                    const totalExpenses = calculateTotalExpenses(expenses);

                    return (
                      <CompactCropChip
                        key={crop.ID}
                        name={crop.Name}
                        expenses={totalExpenses}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {/* Project Description */}
            <Box mt={4}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{
                  color: "#23766a",
                  fontSize: { xs: "1rem", md: "1.25rem" },
                }}
              >
                About This Project
              </Typography>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, rgba(35, 118, 106, 0.05) 0%, rgba(54, 179, 154, 0.05) 100%)",
                  border: "1px solid rgba(54, 179, 154, 0.1)",
                }}
              >
                <CardContent>
                  <Typography
                    color="text.secondary"
                    sx={{ lineHeight: 1.8, textAlign: "justify" }}
                  >
                    {project.Description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </StyledPaper>

        {/* Signup Dialog */}
        <Dialog
          open={openSignup}
          onClose={handleSignupClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              backgroundColor: "#23766a",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Create Your Account
            <IconButton onClick={handleSignupClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {[
                { name: "name", label: "Full Name", md: 12 },
                { name: "email", label: "Email Address", md: 6, type: "email" },
                { name: "phone", label: "Phone Number", md: 6 },
                { name: "state", label: "State", md: 4 },
                { name: "city", label: "City", md: 4 },
                { name: "pincode", label: "Pincode", md: 4 },
                {
                  name: "password",
                  label: "Password",
                  md: 6,
                  type: "password",
                },
                {
                  name: "confirmPassword",
                  label: "Confirm Password",
                  md: 6,
                  type: "password",
                },
              ].map((field) => (
                <Grid key={field.name} item xs={12} md={field.md}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name={field.name}
                    label={field.label}
                    type={field.type || "text"}
                    value={signupData[field.name]}
                    onChange={handleChange}
                    error={
                      !!formErrors[field.name] ||
                      (field.name === "confirmPassword" && passwordError)
                    }
                    helperText={
                      formErrors[field.name] ||
                      (field.name === "confirmPassword" && passwordError
                        ? "Passwords do not match"
                        : "")
                    }
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#1a5c54",
                        },
                        "&:hover fieldset": {
                          borderColor: "#1a5c54",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1a5c54",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#1a5c54",
                        "&.Mui-focused": {
                          color: "#1a5c54",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        padding: { xs: "10.5px 14px", sm: "12.5px 14px" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        transform: {
                          xs: "translate(14px, 12px) scale(1)",
                          sm: "translate(14px, 16px) scale(1)",
                        },
                      },
                      "& .MuiInputLabel-shrink": {
                        transform: {
                          xs: "translate(14px, -6px) scale(0.75)",
                          sm: "translate(14px, -9px) scale(0.75)",
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        lineHeight: { xs: "1.2", sm: "1.4375em" },
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            {apiError && (
              <Typography
                color="error"
                sx={{ mt: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                {apiError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleSignupClose}
              disabled={loading}
              variant="outlined"
              sx={{
                mr: 2,
                color: "#1a5c54",
                borderColor: "#1a5c54",
                "&:hover": {
                  borderColor: "#1a5c54",
                  backgroundColor: "rgba(26, 92, 84, 0.04)",
                },
                "&.Mui-disabled": {
                  color: "rgba(26, 92, 84, 0.38)",
                  borderColor: "rgba(26, 92, 84, 0.38)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              variant="contained"
              sx={{
                backgroundColor: "#23766a",
                "&:hover": { backgroundColor: "#1a5c54" },
                width: "30vh",
                height: "5vh",
                position: "relative",
                padding: "10px 20px",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "white" }} />
                </Box>
              ) : (
                "Sign Up & Continue"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Footer ref={footerRef} />
      </Box>
    </HelmetProvider>
  );
};

export default MoreDetails;